import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { getImageUrl } from '../lib/utils';
import { MdArticle, MdCalendarToday, MdPerson, MdArrowBack } from 'react-icons/md';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const { data } = await api.get(`/blog/${slug}`);
        setPost(data.data);
      } catch (error) {
        console.error('Failed to fetch blog post', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B365D]"></div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-20 px-4 text-center">
        <MdArticle className="h-16 w-16 text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Post not found</h1>
        <p className="text-gray-500 mb-6">This blog post may have been removed or unpublished.</p>
        <Link to="/blog" className="inline-flex items-center text-[#1B365D] font-medium hover:text-[#152A4A]">
          <MdArrowBack className="mr-1.5 h-4 w-4" />
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <Link to="/blog" className="inline-flex items-center text-sm text-[#1B365D] font-medium hover:text-[#152A4A] mb-6">
          <MdArrowBack className="mr-1.5 h-4 w-4" />
          Back to Blog
        </Link>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {post.image && (
            <div className="w-full max-h-96 bg-gray-50">
              <img
                src={getImageUrl(post.image)}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-6 md:p-10">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{post.title}</h1>

            <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500 mb-8 pb-6 border-b border-gray-100">
              <div className="flex items-center">
                <MdCalendarToday className="mr-1.5 h-4 w-4" />
                {new Date(post.publishedAt || post.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              {post.author && (
                <div className="flex items-center">
                  <MdPerson className="mr-1.5 h-4 w-4" />
                  {post.author}
                </div>
              )}
            </div>

            <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
              {post.content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetailPage;
